import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Mail, Lock, Key, Loader2, CheckCircle, AlertCircle, XCircle } from "lucide-react";
import { AppLogo, AppBrandingText } from "@/components/app-logo";
import { useMutation, useQuery } from "@tanstack/react-query";
import { PasswordRequirementsChecklist, PasswordStrengthIndicator } from "@/components/password-requirements";
import { validatePassword } from "@/lib/password-validation";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { getRoleLabel } from "@shared/role-labels";
import { useLocation, useSearch } from "wouter";
import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";

interface InviteVerification {
  valid: boolean;
  email?: string;
  role?: string;
  expiresAt?: string;
  reason?: "not_found" | "expired" | "used" | "cancelled";
}

const inviteSchema = z.object({
  token: z.string().min(1, "Invitation code is required"),
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
  confirmPassword: z.string().min(1, "Please confirm your password"),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
}).refine((data) => validatePassword(data.password).isValid, {
  message: "Password does not meet requirements",
  path: ["password"],
});

type InviteFormData = z.infer<typeof inviteSchema>;

export default function Invite() {
  usePageTitle("Accept Invite");
  const [, navigate] = useLocation();
  const searchString = useSearch();
  const { toast } = useToast();
  const [isSuccess, setIsSuccess] = useState(false);

  // Parse token from URL query params
  const urlParams = new URLSearchParams(searchString);
  const tokenFromUrl = urlParams.get("token") || "";

  const form = useForm<InviteFormData>({
    resolver: zodResolver(inviteSchema),
    defaultValues: {
      token: tokenFromUrl,
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  // Verify token on load if present in URL
  const { data: verification, isLoading: isVerifying, error: verificationError } = useQuery<InviteVerification>({
    queryKey: ["/api/invites/verify", tokenFromUrl],
    queryFn: async () => {
      if (!tokenFromUrl) {
        return { valid: false, reason: "not_found" as const };
      }
      const response = await fetch(`/api/invites/verify/${tokenFromUrl}`);
      if (!response.ok) {
        throw new Error("Failed to verify invitation");
      }
      return response.json();
    },
    enabled: !!tokenFromUrl,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Update email field when verification succeeds
  useEffect(() => {
    if (verification?.valid && verification.email) {
      form.setValue("email", verification.email);
    }
  }, [verification, form]);

  const registerMutation = useMutation({
    mutationFn: async (data: InviteFormData) => {
      return apiRequest("POST", "/api/auth/register", {
        token: data.token,
        email: data.email,
        password: data.password,
      });
    },
    onSuccess: () => {
      setIsSuccess(true);
      toast({
        title: "Account created successfully",
        description: "You can now sign in with your credentials",
      });
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    },
    onError: (error: any) => {
      toast({
        title: "Registration failed",
        description: error.message || "Invalid invitation code or email already registered",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: InviteFormData) => {
    registerMutation.mutate(data);
  };

  // Helper to get error message for invalid tokens
  const getInvalidTokenMessage = (reason?: string) => {
    switch (reason) {
      case "expired":
        return "This invitation has expired. Please contact an administrator for a new invitation.";
      case "used":
        return "This invitation has already been used. If you've already registered, please sign in.";
      case "cancelled":
        return "This invitation has been cancelled. Please contact an administrator for a new invitation.";
      case "not_found":
      default:
        return "This invitation link is invalid. Please check the link or contact an administrator.";
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-success/10">
                  <CheckCircle className="h-8 w-8 text-success" />
                </div>
              </div>
              <h2 className="text-2xl font-semibold">Account Created!</h2>
              <p className="text-muted-foreground">
                Your account has been successfully created. Redirecting you to the login page...
              </p>
              <Button variant="outline" asChild>
                <Link href="/login">Go to Login</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading state while verifying token
  if (tokenFromUrl && isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <AppLogo size="2xl" />
            </div>
            <h1 className="text-3xl">
              <AppBrandingText />
            </h1>
          </div>
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Show error state for invalid tokens
  if (tokenFromUrl && verification && !verification.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <AppLogo size="2xl" />
            </div>
            <h1 className="text-3xl">
              <AppBrandingText />
            </h1>
          </div>
          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
                    <XCircle className="h-8 w-8 text-destructive" />
                  </div>
                </div>
                <h2 className="text-2xl font-semibold">Invalid Invitation</h2>
                <p className="text-muted-foreground">
                  {getInvalidTokenMessage(verification.reason)}
                </p>
                <div className="pt-4 space-y-2">
                  <Button variant="outline" asChild className="w-full">
                    <Link href="/login">Go to Login</Link>
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Gate E6.10: Logo and Title - Updated branding */}
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <AppLogo size="2xl" />
          </div>
          <h1 className="text-3xl" data-testid="text-invite-title">
            <AppBrandingText />
          </h1>
          <p className="text-muted-foreground mt-2" data-testid="text-invite-subtitle">Behavior Evals, Tracking, & Insights</p>
        </div>

        {/* Show role badge if token is valid */}
        {verification?.valid && verification.role && (
          <Alert className="border-primary/50 bg-primary/5">
            <AlertCircle className="h-4 w-4 text-primary" />
            <AlertTitle>Invitation Verified</AlertTitle>
            <AlertDescription>
              You are being invited as a user with <strong>{getRoleLabel(verification.role)}</strong> permissions. Complete the form below to create your account.
            </AlertDescription>
          </Alert>
        )}

        {/* Registration Card */}
        <Card>
          <CardHeader>
            <CardTitle>Create Your Account</CardTitle>
            <CardDescription>
              {tokenFromUrl 
                ? "Complete the form below to create your account"
                : "Enter your invitation code and create your account credentials"
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                {/* Only show token field if not pre-filled from URL */}
                {!tokenFromUrl && (
                  <FormField
                    control={form.control}
                    name="token"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Invitation Code</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              {...field}
                              type="text"
                              placeholder="Enter your invitation code"
                              className="pl-10"
                              data-testid="input-invite-token"
                              disabled={registerMutation.isPending}
                            />
                          </div>
                        </FormControl>
                        <FormDescription>
                          This code was sent to you by an administrator
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            type="email"
                            placeholder="Enter your email address..."
                            className={`pl-10 ${verification?.valid ? "bg-muted" : ""}`}
                            data-testid="input-register-email"
                            disabled={registerMutation.isPending}
                            readOnly={verification?.valid}
                          />
                        </div>
                      </FormControl>
                      {verification?.valid && (
                        <FormDescription>
                          Email is pre-filled from your invitation
                        </FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            type="password"
                            placeholder="••••••••"
                            className="pl-10"
                            data-testid="input-register-password"
                            disabled={registerMutation.isPending}
                          />
                        </div>
                      </FormControl>
                      <PasswordStrengthIndicator password={field.value} />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="bg-muted/50 rounded-lg p-3">
                  <PasswordRequirementsChecklist password={form.watch("password")} />
                </div>

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Confirm Password</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            {...field}
                            type="password"
                            placeholder="••••••••"
                            className="pl-10"
                            data-testid="input-confirm-password"
                            disabled={registerMutation.isPending}
                          />
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full"
                  disabled={
                    registerMutation.isPending ||
                    !validatePassword(form.watch("password")).isValid ||
                    form.watch("password") !== form.watch("confirmPassword") ||
                    !form.watch("email")
                  }
                  data-testid="button-register"
                >
                  {registerMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    "Create Account"
                  )}
                </Button>
              </form>
            </Form>

            {/* Login Link */}
            <div className="mt-6 pt-6 border-t text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link href="/login" className="text-primary hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}